import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { useDateFormat } from '../contexts/DateFormatContext';
import DateInput from '../components/DateInput';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { BarChart3, TrendingUp, Target, Award, Download, FileSpreadsheet, FileText, Printer, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';
import BettingSuggestions from '../components/BettingSuggestions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { user } = useContext(AuthContext);
  const { formatDate } = useDateFormat();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmakers, setBookmakers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [customMarkets, setCustomMarkets] = useState([]);
  const [customBookmakers, setCustomBookmakers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [filters, setFilters] = useState({
    league: '',
    tipster: '',
    strategy: '',
    bookmaker: '',
    customBookmaker: '',
    market: '',
    customMarket: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [filters]);

  useEffect(() => {
    // Close export menu when clicking outside
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('[data-testid="export-data-button"]')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu]);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const [bookmakersRes, marketsRes, leaguesRes, customMarketsRes, customBookmakersRes] = await Promise.all([
        axios.get(`${API}/data/bookmakers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/markets`, { headers: { Authorization: `Bearer ${token}` } }),
        // Load only popular leagues for better performance
        axios.get(`${API}/data/leagues?popular_only=true&limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/my-custom-markets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/my-custom-bookmakers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setBookmakers(bookmakersRes.data.bookmakers);
      setMarkets(marketsRes.data.markets);
      setLeagues(leaguesRes.data.leagues);
      setCustomMarkets(customMarketsRes.data.markets || []);
      setCustomBookmakers(customBookmakersRes.data.bookmakers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('session_token');
    const params = new URLSearchParams();
    if (filters.league && filters.league !== 'all') params.append('league', filters.league);
    if (filters.tipster) params.append('tipster', filters.tipster);
    if (filters.strategy) params.append('strategy', filters.strategy);
    // Use custom bookmaker if "Custom" is selected
    const finalBookmaker = filters.bookmaker === 'Custom' ? filters.customBookmaker : filters.bookmaker;
    if (finalBookmaker && finalBookmaker !== 'all') params.append('bookmaker', finalBookmaker);
    // Use custom market if "Custom" is selected
    const finalMarket = filters.market === 'Custom' ? filters.customMarket : filters.market;
    if (finalMarket && finalMarket !== 'all') params.append('market', finalMarket);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    try {
      const [statsRes, chartsRes] = await Promise.all([
        axios.get(`${API}/reports/statistics?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/charts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setChartData(chartsRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async (format = 'csv') => {
    // Check if user has premium or tipster subscription
    if (!user?.subscription_plan || user.subscription_plan === 'free') {
      toast.error('Export Data is a Premium feature. Please upgrade to access this feature.');
      return;
    }

    setShowExportMenu(false);

    const token = localStorage.getItem('session_token');
    const params = new URLSearchParams();
    if (filters.league && filters.league !== 'all') params.append('league', filters.league);
    if (filters.tipster) params.append('tipster', filters.tipster);
    if (filters.strategy) params.append('strategy', filters.strategy);
    const finalBookmaker = filters.bookmaker === 'Custom' ? filters.customBookmaker : filters.bookmaker;
    if (finalBookmaker && finalBookmaker !== 'all') params.append('bookmaker', finalBookmaker);
    const finalMarket = filters.market === 'Custom' ? filters.customMarket : filters.market;
    if (finalMarket && finalMarket !== 'all') params.append('market', finalMarket);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    try {
      toast.loading('Exporting data...');
      const response = await axios.get(`${API}/bets/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bets = response.data.bets;
      if (!bets || bets.length === 0) {
        toast.dismiss();
        toast.info('No data to export with current filters');
        return;
      }

      // Create data headers
      const headers = [
        'Date',
        'Match/Event',
        'League',
        'Market',
        'Selection',
        'Odds',
        'Stake',
        'Bookmaker',
        'Tipster',
        'Strategy',
        'Outcome',
        'Returns',
        'Profit/Loss',
        'Notes'
      ];

      // Create data rows
      const rows = bets.map(bet => [
        formatDate(bet.date),
        bet.match_event || '',
        bet.league || '',
        bet.market || '',
        bet.selection || '',
        bet.odds || '',
        bet.stake || '',
        bet.bookmaker || '',
        bet.tipster || '',
        bet.strategy || '',
        bet.outcome || 'pending',
        bet.returns || '0',
        bet.profit_loss || '0',
        bet.notes || ''
      ]);

      // Generate filename
      const baseFilename = filters.start_date && filters.end_date
        ? `betting-data-${filters.start_date}-to-${filters.end_date}`
        : `betting-data-${new Date().toISOString().split('T')[0]}`;

      if (format === 'csv') {
        exportCSV(headers, rows, baseFilename);
      } else if (format === 'excel') {
        exportExcel(headers, rows, baseFilename);
      } else if (format === 'pdf') {
        exportPDF(headers, rows, baseFilename);
      } else if (format === 'print') {
        printData(headers, rows);
      }

      toast.dismiss();
      toast.success(`Exported ${bets.length} bets successfully!`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.dismiss();
      toast.error('Failed to export data. Please try again.');
    }
  };

  const exportCSV = (headers, rows, filename) => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportExcel = (headers, rows, filename) => {
    // Create HTML table for Excel
    const tableHtml = `
      <table>
        <thead>
          <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
        </tbody>
      </table>
    `;

    const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = (headers, rows, filename) => {
    // Create printable content
    const content = `
      <html>
        <head>
          <title>${filename}</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }
            th { background-color: #4CAF50; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            h1 { text-align: center; }
          </style>
        </head>
        <body>
          <h1>Betting Report</h1>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    
    // Wait for content to load then trigger print and close
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 100);
    }, 250);
  };

  const printData = (headers, rows) => {
    const content = `
      <html>
        <head>
          <title>Betting Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 11px; }
            th { background-color: #4CAF50; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            h1 { text-align: center; color: #333; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Betting Report</h1>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading reports...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="reports-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Reports & Analytics</h1>
            
            {/* Export Button with Dropdown - Always visible */}
            <div className="relative">
              {user?.subscription_plan && user.subscription_plan !== 'free' ? (
                <>
                  <Button
                    data-testid="export-data-button"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Data
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-50">
                      <div className="py-1">
                        <button
                          onClick={() => handleExportData('csv')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <FileText className="w-4 h-4" />
                          Export as CSV
                        </button>
                        <button
                          onClick={() => handleExportData('excel')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          Export as Excel
                        </button>
                        <button
                          onClick={() => handleExportData('pdf')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <FileText className="w-4 h-4" />
                          Export as PDF
                        </button>
                        <button
                          onClick={() => handleExportData('print')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <Printer className="w-4 h-4" />
                          Print Report
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="relative group">
                  <Button
                    data-testid="export-data-button-free"
                    disabled
                    className="bg-gray-600 text-gray-400 cursor-not-allowed flex items-center gap-2 relative"
                  >
                    <Download className="w-4 h-4" />
                    Export Data
                    <span className="ml-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      PRO
                    </span>
                  </Button>
                  <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-yellow-500/30 rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <p className="text-sm text-white mb-2">
                      <span className="font-semibold text-yellow-400">Premium Feature</span>
                    </p>
                    <p className="text-xs text-gray-300">
                      Export your betting data in multiple formats (CSV, Excel, PDF) or print reports. Upgrade to Premium to unlock this feature!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Betting Analysis */}
          <BettingSuggestions />

          {/* Filters */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                <div className="relative group flex flex-col">
                  <Label htmlFor="league" className="text-white flex items-center gap-2 h-6">
                    League
                    {(!user?.subscription_plan || user.subscription_plan === 'free') && (
                      <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        PRO
                      </span>
                    )}
                  </Label>
                  <Select 
                    value={filters.league} 
                    onValueChange={(value) => {
                      if (!user?.subscription_plan || user.subscription_plan === 'free') {
                        toast.error('Detailed reports by league is a Premium feature. Please upgrade to access this filter.');
                        return;
                      }
                      setFilters({ ...filters, league: value });
                    }}
                    disabled={!user?.subscription_plan || user.subscription_plan === 'free'}
                  >
                    <SelectTrigger 
                      data-testid="filter-league" 
                      className={`mt-2 ${
                        (!user?.subscription_plan || user.subscription_plan === 'free')
                          ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-white/5 border-white/10 text-white'
                      }`}
                    >
                      <SelectValue placeholder="All Leagues" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="all">All Leagues</SelectItem>
                      {leagues.map((league) => (
                        <SelectItem key={league} value={league}>{league}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(!user?.subscription_plan || user.subscription_plan === 'free') && (
                    <div className="absolute left-0 top-full mt-2 w-64 bg-slate-800 border border-yellow-500/30 rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <p className="text-sm text-white mb-2">
                        <span className="font-semibold text-yellow-400">Premium Feature</span>
                      </p>
                      <p className="text-xs text-gray-300">
                        Filter your reports by specific leagues to analyze performance in different competitions. Upgrade to Premium to unlock this feature!
                      </p>
                    </div>
                  )}
                </div>
                <div className="relative group flex flex-col">
                  <Label htmlFor="tipster" className="text-white flex items-center gap-2 h-6">
                    Tipster
                    {(!user?.subscription_plan || user.subscription_plan === 'free') && (
                      <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        PRO
                      </span>
                    )}
                  </Label>
                  <Input
                    data-testid="filter-tipster"
                    id="tipster"
                    value={filters.tipster}
                    onChange={(e) => {
                      if (!user?.subscription_plan || user.subscription_plan === 'free') {
                        toast.error('Detailed reports by tipster is a Premium feature. Please upgrade to access this filter.');
                        return;
                      }
                      setFilters({ ...filters, tipster: e.target.value });
                    }}
                    className={`mt-2 ${
                      (!user?.subscription_plan || user.subscription_plan === 'free')
                        ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-white/5 border-white/10 text-white'
                    }`}
                    placeholder="Filter by tipster"
                    disabled={!user?.subscription_plan || user.subscription_plan === 'free'}
                  />
                  {(!user?.subscription_plan || user.subscription_plan === 'free') && (
                    <div className="absolute left-0 top-full mt-2 w-64 bg-slate-800 border border-yellow-500/30 rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <p className="text-sm text-white mb-2">
                        <span className="font-semibold text-yellow-400">Premium Feature</span>
                      </p>
                      <p className="text-xs text-gray-300">
                        Filter your reports by specific tipsters to track which sources provide the best results. Upgrade to Premium to unlock this feature!
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="strategy" className="text-white h-6 flex items-center">Strategy</Label>
                  <Input
                    data-testid="filter-strategy"
                    id="strategy"
                    value={filters.strategy}
                    onChange={(e) => setFilters({ ...filters, strategy: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="Filter by strategy"
                  />
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="bookmaker" className="text-white h-6 flex items-center">Bookmaker</Label>
                  <Select value={filters.bookmaker} onValueChange={(value) => setFilters({ ...filters, bookmaker: value })}>
                    <SelectTrigger data-testid="filter-bookmaker" className="bg-white/5 border-white/10 text-white mt-2">
                      <SelectValue placeholder="All Bookmakers" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="all">All Bookmakers</SelectItem>
                      {bookmakers.map((bookmaker) => (
                        <SelectItem key={bookmaker} value={bookmaker}>{bookmaker}</SelectItem>
                      ))}
                      {customBookmakers.map((bookmaker) => (
                        <SelectItem key={`custom-${bookmaker}`} value={bookmaker}>{bookmaker} ⭐</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filters.bookmaker === 'Custom' && (
                    <Input
                      data-testid="custom-bookmaker-filter"
                      value={filters.customBookmaker}
                      onChange={(e) => setFilters({ ...filters, customBookmaker: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="Enter custom bookmaker"
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="market" className="text-white h-6 flex items-center">Market</Label>
                  <Select value={filters.market} onValueChange={(value) => setFilters({ ...filters, market: value })}>
                    <SelectTrigger data-testid="filter-market" className="bg-white/5 border-white/10 text-white mt-2">
                      <SelectValue placeholder="All Markets" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="all">All Markets</SelectItem>
                      {markets.map((market) => (
                        <SelectItem key={market} value={market}>{market}</SelectItem>
                      ))}
                      {customMarkets.map((market) => (
                        <SelectItem key={market} value={market}>{market} ⭐</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filters.market === 'Custom' && (
                    <Input
                      data-testid="custom-market-filter"
                      value={filters.customMarket}
                      onChange={(e) => setFilters({ ...filters, customMarket: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="Enter custom market"
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <DateInput
                    label="Start Date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    className=""
                  />
                </div>
                <div className="flex flex-col">
                  <DateInput
                    label="End Date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    className=""
                  />
                </div>
              </div>
              <Button
                data-testid="clear-filters-button"
                onClick={() => setFilters({ league: '', tipster: '', strategy: '', bookmaker: '', customBookmaker: '', market: '', customMarket: '', start_date: '', end_date: '' })}
                variant="outline"
                className="mt-4 border-white/20 text-white hover:bg-white/10"
              >
                Clear Filters
              </Button>
            </CardContent>
          </Card>

          {/* Overall Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 border-blue-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Bets</p>
                    <p data-testid="stat-total-bets" className="text-3xl font-bold text-white">{stats?.total_bets || 0}</p>
                  </div>
                  <Target className="w-12 h-12 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-green-700/20 border-green-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Win Rate</p>
                    <p data-testid="stat-win-rate" className="text-3xl font-bold text-white">{stats?.win_rate || 0}%</p>
                    <p className="text-xs text-gray-400 mt-1">{stats?.wins || 0}W - {stats?.losses || 0}L</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border-purple-500/30 stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">ROI</p>
                    <p data-testid="stat-roi" className="text-3xl font-bold text-white">{stats?.roi || 0}%</p>
                  </div>
                  <BarChart3 className="w-12 h-12 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${stats?.total_returns >= 0 ? 'from-emerald-500/20 to-emerald-700/20 border-emerald-500/30' : 'from-red-500/20 to-red-700/20 border-red-500/30'} stat-card`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Profit/Loss</p>
                    <p data-testid="stat-profit-loss" className="text-3xl font-bold text-white">
                      <Price amount={stats?.total_returns || 0} />
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Staked: ${stats?.total_staked?.toFixed(2) || '0.00'}</p>
                  </div>
                  <Award className={`w-12 h-12 ${stats?.total_returns >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section - Filtered by Date Range */}
          {chartData && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Performance Charts</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Daily Profit/Loss Chart */}
                <Card className="glass-dark border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Daily Profit/Loss</CardTitle>
                    {filters.start_date && filters.end_date && (
                      <p className="text-sm text-gray-400">
                        {new Date(filters.start_date).toLocaleDateString()} - {new Date(filters.end_date).toLocaleDateString()}
                      </p>
                    )}
                    {!filters.start_date && !filters.end_date && (
                      <p className="text-sm text-gray-400">All Time</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {chartData.daily_data && chartData.daily_data.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-center mb-4">
                          <p className="text-sm text-gray-400">Final Cumulative</p>
                          <p className={`text-3xl font-bold ${chartData.daily_data[chartData.daily_data.length - 1]?.cumulative >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <Price amount={chartData.daily_data[chartData.daily_data.length - 1]?.cumulative || 0} />
                          </p>
                        </div>
                        <div className="grid grid-cols-5 gap-1 text-xs">
                          {chartData.daily_data.slice(-10).map((day, idx) => (
                            <div key={idx} className="glass p-2 rounded text-center">
                              <p className="text-gray-400">{day.date.split('-')[2]}</p>
                              <p className={`font-bold ${day.cumulative >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                <Price amount={day.cumulative} decimals={0} />
                              </p>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">Last {Math.min(10, chartData.daily_data.length)} days shown</p>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-16">No data available for selected filters</p>
                    )}
                  </CardContent>
                </Card>

                {/* Outcome Summary */}
                <Card className="glass-dark border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Outcome Summary</CardTitle>
                    {filters.start_date && filters.end_date && (
                      <p className="text-sm text-gray-400">
                        {new Date(filters.start_date).toLocaleDateString()} - {new Date(filters.end_date).toLocaleDateString()}
                      </p>
                    )}
                    {!filters.start_date && !filters.end_date && (
                      <p className="text-sm text-gray-400">All Time</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {chartData.outcome_summary && chartData.outcome_summary.length > 0 ? (
                      <div className="space-y-4">
                        {chartData.outcome_summary.map((item, idx) => (
                          <div key={idx}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-white font-medium">{item.name}</span>
                              <span className="text-gray-400">{item.percentage}% ({item.value} bets)</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-6">
                              <div
                                className={`h-6 rounded-full flex items-center justify-end pr-2 text-white text-sm font-bold ${
                                  item.name === 'Won' ? 'bg-green-500' :
                                  item.name === 'Lost' ? 'bg-red-500' : 'bg-gray-500'
                                }`}
                                style={{ width: `${item.percentage}%` }}
                              >
                                {item.percentage > 10 && `${item.percentage}%`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-16">No data available for selected filters</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Results Chart */}
              <Card className="glass-dark border-white/10 mb-6">
                <CardHeader>
                  <CardTitle className="text-white">Monthly Results</CardTitle>
                  {filters.start_date && filters.end_date && (
                    <p className="text-sm text-gray-400">
                      {new Date(filters.start_date).toLocaleDateString()} - {new Date(filters.end_date).toLocaleDateString()}
                    </p>
                  )}
                  {!filters.start_date && !filters.end_date && (
                    <p className="text-sm text-gray-400">All Time</p>
                  )}
                </CardHeader>
                <CardContent>
                  {chartData.monthly_data && chartData.monthly_data.length > 0 ? (
                    <div className={`grid gap-4 items-end`} style={{
                      height: '200px',
                      gridTemplateColumns: `repeat(${Math.min(chartData.monthly_data.length, 12)}, 1fr)`
                    }}>
                      {chartData.monthly_data.map((month, idx) => {
                        const maxProfit = Math.max(...chartData.monthly_data.map(m => Math.abs(m.profit)));
                        const heightPercent = maxProfit > 0 ? (Math.abs(month.profit) / maxProfit) * 100 : 10;
                        return (
                          <div key={idx} className="flex flex-col items-center justify-end h-full">
                            <div className="text-center mb-2">
                              <p className={`text-sm font-bold ${month.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                <Price amount={month.profit} decimals={0} />
                              </p>
                            </div>
                            <div
                              className={`w-full rounded-t ${month.profit >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                              style={{ height: `${heightPercent}%`, minHeight: '20px' }}
                            ></div>
                            <p className="text-xs text-gray-400 mt-2 text-center">{month.month}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-16">No data available for selected filters</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Performance Breakdown Sections */}
          {/* League Stats */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Performance by League</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.league_stats && Object.keys(stats.league_stats).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.league_stats).map(([league, data]) => (
                    <div key={league} data-testid={`league-stat-${league}`} className="glass p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">{league}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Record</p>
                          <p className="text-white font-medium">{data.wins}W - {data.losses}L</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Win Rate</p>
                          <p className="text-white font-medium">{data.win_rate.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">ROI</p>
                          <p className={`font-medium ${data.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{data.roi.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Profit/Loss</p>
                          <p className={`font-medium ${data.total_returns >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <Price amount={data.total_returns} />
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p data-testid="no-league-stats" className="text-gray-400 text-center py-8">No league data available</p>
              )}
            </CardContent>
          </Card>

          {/* Tipster Stats */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Performance by Tipster</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.tipster_stats && Object.keys(stats.tipster_stats).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.tipster_stats).map(([tipster, data]) => (
                    <div key={tipster} data-testid={`tipster-stat-${tipster}`} className="glass p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">{tipster}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Record</p>
                          <p className="text-white font-medium">{data.wins}W - {data.losses}L</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Win Rate</p>
                          <p className="text-white font-medium">{data.win_rate.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">ROI</p>
                          <p className={`font-medium ${data.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{data.roi.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Profit/Loss</p>
                          <p className={`font-medium ${data.total_returns >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <Price amount={data.total_returns} />
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p data-testid="no-tipster-stats" className="text-gray-400 text-center py-8">No tipster data available</p>
              )}
            </CardContent>
          </Card>

          {/* Bookmaker Stats */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Performance by Bookmaker</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.bookmaker_stats && Object.keys(stats.bookmaker_stats).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.bookmaker_stats).map(([bookmaker, data]) => (
                    <div key={bookmaker} data-testid={`bookmaker-stat-${bookmaker}`} className="glass p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">{bookmaker}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Record</p>
                          <p className="text-white font-medium">{data.wins}W - {data.losses}L</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Win Rate</p>
                          <p className="text-white font-medium">{data.win_rate.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">ROI</p>
                          <p className={`font-medium ${data.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{data.roi.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Profit/Loss</p>
                          <p className={`font-medium ${data.total_returns >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <Price amount={data.total_returns} />
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p data-testid="no-bookmaker-stats" className="text-gray-400 text-center py-8">No bookmaker data available</p>
              )}
            </CardContent>
          </Card>

          {/* Market Stats */}
          <Card className="glass-dark border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Performance by Market</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.market_stats && Object.keys(stats.market_stats).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.market_stats).map(([market, data]) => (
                    <div key={market} data-testid={`market-stat-${market}`} className="glass p-4 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">{market}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Record</p>
                          <p className="text-white font-medium">{data.wins}W - {data.losses}L</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Win Rate</p>
                          <p className="text-white font-medium">{data.win_rate.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">ROI</p>
                          <p className={`font-medium ${data.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{data.roi.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Profit/Loss</p>
                          <p className={`font-medium ${data.total_returns >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <Price amount={data.total_returns} />
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p data-testid="no-market-stats" className="text-gray-400 text-center py-8">No market data available</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
